import React, { useCallback, useEffect, useState } from "react";
import { Receipt as ReceiptIcon } from "lucide-react";
import EmployeeShell from "../../components/layout/EmployeeShell";
import EmptyState from "../../components/EmptyState";
import ReceiptDetailDrawer from "../../components/ReceiptDetailDrawer";
import StatusBadge from "../../components/StatusBadge";
import { useI18n } from "../../i18n";
import { useAuth } from "../../context/AuthContext";
import { ReceiptsApi } from "../../lib/api";
import { categoryLabel, formatDate, formatMoney } from "../../lib/format";
import { Skeleton } from "../../components/ui/skeleton";

const FILTERS = [
  { key: "all", labelKey: "employee.filterAll" },
  { key: "queued,processing", labelKey: "employee.filterScanning" },
  { key: "needs_review", labelKey: "employee.filterReview" },
  { key: "approved", labelKey: "employee.filterApproved" },
];

const EmployeeReceipts = () => {
  const { t } = useI18n();
  const { currency, categories, isAccountingFirm } = useAuth();
  const [status, setStatus] = useState("all");
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (status !== "all") params.status = status;
      const data = await ReceiptsApi.list(params);
      setReceipts(data.receipts || []);
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <EmployeeShell title={t("employee.myReceiptsTitle")} subtitle={t("employee.myReceiptsSub")}>
      <div className="flex flex-wrap gap-1.5">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setStatus(f.key)}
            data-testid={`employee-filter-${f.key.replace(/,/g, "-")}`}
            className={`rounded-full border px-3 py-1.5 text-xs font-medium ${
              status === f.key
                ? "border-primary bg-primary/10 text-primary"
                : "border-border text-muted-foreground"
            }`}
          >
            {t(f.labelKey)}
          </button>
        ))}
      </div>

      <div className="mt-4 space-y-2">
        {loading && !receipts.length ? (
          Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-20 rounded-xl" />)
        ) : receipts.length === 0 ? (
          <EmptyState
            icon={ReceiptIcon}
            title={t("employee.emptyTitle")}
            description={t("employee.emptyBody")}
            actionLabel={t("receipts.scanCta")}
            onAction={() => {
              window.location.href = "/me";
            }}
          />
        ) : (
          receipts.map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => setOpenId(r.id)}
              className="ll-card flex w-full items-center justify-between gap-3 p-3 text-left"
              data-testid="employee-receipt-item"
            >
              <div className="min-w-0">
                <p className="truncate font-medium">{r.vendor_name || r.filename || "Receipt"}</p>
                <p className="truncate text-xs text-muted-foreground">
                  {r.issue_date ? formatDate(r.issue_date) : t("employee.datePending")}
                  {r.category ? ` · ${categoryLabel(r.category, categories)}` : ""}
                </p>
                <div className="mt-1.5">
                  <StatusBadge status={r.status} />
                </div>
              </div>
              <div className="shrink-0 text-right">
                <p className="ll-num font-display text-base font-semibold">
                  {r.gross_total != null ? formatMoney(r.gross_total, r.currency || currency) : "—"}
                </p>
                <p className="ll-num text-xs text-muted-foreground">
                  {t("common.vat")} {r.vat_total != null ? formatMoney(r.vat_total, r.currency || currency) : "—"}
                </p>
                {!isAccountingFirm ? (
                  <p className="ll-num text-xs font-medium text-primary">
                    {t("common.refund").toLowerCase()} {formatMoney(r.refund?.amount, r.currency || currency)}
                  </p>
                ) : null}
              </div>
            </button>
          ))
        )}
      </div>

      <ReceiptDetailDrawer
        receiptId={openId}
        open={Boolean(openId)}
        onClose={() => setOpenId(null)}
        onChanged={load}
        isAdmin={false}
        categories={categories}
        currency={currency}
      />
    </EmployeeShell>
  );
};

export default EmployeeReceipts;
